import { useCallback, useEffect, useRef, useState, type SyntheticEvent } from 'react';
import { formatDate } from '../../../lib/format/dates.ts';
import { api, type Physician } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy } from '../../copy/en-CA.ts';
import { checkPhysician, describePhysicianFailure, describePhysicianProblem, type PhysicianField } from '../../describe.ts';

const c = copy.admin.physicians;

interface Problem {
  message: string;
  field: PhysicianField | null;
}

/** The props that tie a field to the problem shown under it, so a screen reader hears both. */
function invalidProps(problem: Problem | null, field: PhysicianField, errorId: string) {
  return problem?.field === field ? { 'aria-invalid': true, 'aria-describedby': errorId } : {};
}

function Row({ p, onSaved }: { p: Physician; onSaved: () => Promise<void> }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(p.name);
  const [initials, setInitials] = useState(p.initials);
  const [colour, setColour] = useState(p.colour);
  const [startDate, setStartDate] = useState(p.startDate ?? '');
  const [endDate, setEndDate] = useState(p.endDate ?? '');
  const [error, setError] = useState<Problem | null>(null);
  const [busy, setBusy] = useState(false);

  const editButton = useRef<HTMLButtonElement>(null);
  const nameField = useRef<HTMLInputElement>(null);
  const initialsField = useRef<HTMLInputElement>(null);
  const endField = useRef<HTMLInputElement>(null);
  const fieldFor = { name: nameField, initials: initialsField, endDate: endField };
  // Where keyboard focus goes once the row has switched between showing and
  // editing: the Name field on Edit, back to this row's Edit button after.
  const focusNext = useRef<'name' | 'edit' | null>(null);
  useEffect(() => {
    if (focusNext.current === 'name') nameField.current?.focus();
    if (focusNext.current === 'edit') editButton.current?.focus();
    focusNext.current = null;
  }, [editing]);

  const errorId = `phys-${p.id}-error`;

  /** Back to what is saved, so an abandoned edit is never saved later by accident. */
  const resetFields = () => {
    setName(p.name);
    setInitials(p.initials);
    setColour(p.colour);
    setStartDate(p.startDate ?? '');
    setEndDate(p.endDate ?? '');
    setError(null);
  };

  const startEditing = () => {
    resetFields();
    focusNext.current = 'name';
    setEditing(true);
  };

  const stopEditing = () => {
    focusNext.current = 'edit';
    setEditing(false);
  };

  const cancel = () => {
    resetFields();
    stopEditing();
  };

  const showProblem = (problem: Problem) => {
    setError(problem);
    if (problem.field) fieldFor[problem.field].current?.focus();
  };

  const save = async () => {
    const problem = checkPhysician({ name, initials, startDate, endDate });
    if (problem) {
      showProblem(describePhysicianProblem(problem));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.admin.updatePhysician(p.id, { name: name.trim(), initials: initials.trim(), colour, startDate: startDate || null, endDate: endDate || null });
      await onSaved();
      stopEditing();
    } catch (err) {
      showProblem(describePhysicianFailure(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async () => {
    if (p.active && !window.confirm(c.confirmDeactivate(p.name))) return;
    setError(null);
    try {
      await api.admin.updatePhysician(p.id, { active: !p.active });
      await onSaved();
    } catch (err) {
      setError(describePhysicianFailure(err));
    }
  };

  if (editing) {
    return (
      <tr>
        <td colSpan={4}>
          {/* On a phone the table is wider than the screen and scrolls sideways; this keeps the
              fields and any message within the part that shows (page gutter, border and cell
              padding come to about 4rem), so a message is never cut off at the right. */}
          <div className="stack" style={{ position: 'sticky', left: '0.9rem', maxWidth: 'calc(100vw - 4rem)' }}>
            <div className="row">
              <div className="field grow">
                <label htmlFor={`name-${p.id}`}>{c.name}</label>
                <input
                  id={`name-${p.id}`}
                  ref={nameField}
                  className="input"
                  maxLength={120}
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                  }}
                  {...invalidProps(error, 'name', errorId)}
                />
              </div>
              <div className="field" style={{ width: 90 }}>
                <label htmlFor={`init-${p.id}`}>{c.initials}</label>
                <input
                  id={`init-${p.id}`}
                  ref={initialsField}
                  className="input"
                  maxLength={4}
                  value={initials}
                  onChange={(e) => {
                    setInitials(e.target.value);
                  }}
                  {...invalidProps(error, 'initials', errorId)}
                />
              </div>
              <div className="field" style={{ width: 90 }}>
                <label htmlFor={`col-${p.id}`}>{c.colour}</label>
                <input id={`col-${p.id}`} className="input" type="color" value={colour} onChange={(e) => { setColour(e.target.value); }} />
              </div>
              <div className="field">
                <label htmlFor={`start-${p.id}`}>{c.startDate}</label>
                <input id={`start-${p.id}`} className="input" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); }} />
              </div>
              <div className="field">
                <label htmlFor={`end-${p.id}`}>{c.endDate}</label>
                <input
                  id={`end-${p.id}`}
                  ref={endField}
                  className="input"
                  type="date"
                  min={startDate || undefined}
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                  }}
                  {...invalidProps(error, 'endDate', errorId)}
                />
              </div>
            </div>
            {error ? (
              <div id={errorId}>
                <Notice kind="error">{error.message}</Notice>
              </div>
            ) : null}
            <div className="row">
              <button type="button" className="btn primary small" disabled={busy} onClick={() => void save()}>
                {c.save}
              </button>
              <button type="button" className="btn small" disabled={busy} onClick={cancel}>
                {c.cancel}
              </button>
            </div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>
        <span className="physician">
          <span className="dot" style={{ background: p.colour }} aria-hidden="true" />
          <span className="physician-name">{p.name}</span>
          <span className="muted small">{p.initials}</span>
        </span>
      </td>
      <td className="muted">
        {p.startDate ? formatDate(p.startDate) : c.blank}
        {' – '}
        {p.endDate ? formatDate(p.endDate) : c.staying}
      </td>
      <td>{p.active ? <span className="badge">{c.status.active}</span> : <span className="badge inactive">{c.status.inactive}</span>}</td>
      <td>
        <div className="row">
          <button type="button" ref={editButton} className="btn small" onClick={startEditing}>
            {c.edit}
          </button>
          <button type="button" className={`btn small ${p.active ? 'danger' : ''}`} onClick={() => void toggleActive()}>
            {p.active ? c.deactivate : c.reactivate}
          </button>
        </div>
        {error ? (
          <div style={{ marginTop: '0.5rem' }}>
            <Notice kind="error">{error.message}</Notice>
          </div>
        ) : null}
      </td>
    </tr>
  );
}

export function AdminPhysiciansPage() {
  const [rows, setRows] = useState<Physician[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<Problem | null>(null);
  const nameField = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const { physicians } = await api.admin.listPhysicians();
      setRows(physicians);
      setLoadError(null);
    } catch (err) {
      setLoadError(describePhysicianFailure(err).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    const showProblem = (problem: Problem) => {
      setFormError(problem);
      if (problem.field === 'name') nameField.current?.focus();
    };
    const problem = checkPhysician({ name, startDate });
    if (problem) {
      showProblem(describePhysicianProblem(problem));
      return;
    }
    setFormError(null);
    setCreating(true);
    try {
      await api.admin.createPhysician({ name: name.trim(), startDate: startDate || null });
      setName('');
      setStartDate('');
      await load();
    } catch (err) {
      showProblem(describePhysicianFailure(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>

      {/* noValidate: an empty name gets the same words as a blank one (checkPhysician), not the browser's. */}
      <form className="card" noValidate onSubmit={(e) => { void create(e); }} aria-labelledby="add-physician">
        <h2 id="add-physician">{c.add}</h2>
        <div className="row">
          <div className="field grow">
            <label htmlFor="new-phys-name">{c.name}</label>
            <input
              id="new-phys-name"
              ref={nameField}
              className="input"
              required
              maxLength={120}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              {...invalidProps(formError, 'name', 'new-phys-error')}
            />
          </div>
          <div className="field">
            <label htmlFor="new-phys-start">{c.startDate}</label>
            <input id="new-phys-start" className="input" type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); }} />
          </div>
        </div>
        {formError ? (
          <div id="new-phys-error">
            <Notice kind="error">{formError.message}</Notice>
          </div>
        ) : null}
        <div>
          <button type="submit" className="btn primary" disabled={creating}>
            {creating ? c.creating : c.create}
          </button>
        </div>
      </form>

      {loadError ? <Notice kind="error">{loadError}</Notice> : null}
      {rows?.length === 0 ? (
        <section className="card">
          <p className="empty">{c.empty}</p>
        </section>
      ) : null}
      {rows && rows.length > 0 ? (
        <div className="table-region" role="region" aria-label={c.title} tabIndex={0}>
          <table>
            <thead>
              <tr>
                <th>{c.columns.name}</th>
                <th>{c.columns.dates}</th>
                <th>{c.columns.status}</th>
                <th>{c.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <Row key={p.id} p={p} onSaved={load} />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}
